import Vue from 'vue'
import {ICable, IPerform, ISubscription} from '../interfaces/actionCableInterfaces'

// actioncable-vue automatically unsubscribes when the component is destroyed
// https://github.com/mclintprojects/actioncable-vue#-unsubscriptions
export default class ActionCableService {
  private readonly cable: ICable
  private readonly debugLevel: 'debug'
  private readonly afterPerform?: (whatToDo: IPerform) => void
  private readonly afterSubscribe?: (subscription: ISubscription) => void
  private readonly afterUnsubscribe?: (channelName: string) => void

  constructor (private readonly vueComponent: Vue, options: {
    debugLevel?: 'debug'
    afterPerform?: Function,
    afterSubscribe?: Function,
    afterUnsubscribe?: Function,
  } = {}) {
    this.cable = vueComponent.$cable
    for (const key in options) {
      this[key] = options[key]
    }
  }

  perform (whatToDo: IPerform): void {
    this.cable.perform(whatToDo)
    this._afterPerform(whatToDo)
  }

  subscribe (subscription: ISubscription, vueChannelName?: string): void {
    if (!this.hasSubscriptions) { // checks if there are any at all, not a specific channel
      this.cable.subscribe(subscription, vueChannelName)
      this._afterSubscribe(subscription)
    } else {
      this.log('ignored subscribe', 'debug')
    }
  }

  unsubscribe (channelName: string): void {
    if (this.hasSubscriptions) {// checks if there are any at all, not a specific channel
      this.cable.unsubscribe(channelName)
      this._afterUnsubscribe(channelName)
    } else {
      this.log('ignored unsubscribe', 'debug')
    }
  }

  private _afterSubscribe (subscription: ISubscription): void {
    this.log('after subscribe', 'debug')
    if (typeof this.afterSubscribe === 'function') {
      this.afterSubscribe(subscription)
    }
  }

  private _afterUnsubscribe (channelName: string): void {
    this.log('after unsubscribe', 'debug')
    if (typeof this.afterUnsubscribe === 'function') {
      this.afterUnsubscribe(channelName)
    }

    this.disconnectIfNotSubscribed()
  }

  private _afterPerform (whatToDo: IPerform): void {
    this.log('after perform', 'debug')
    if (typeof this.afterPerform === 'function') {
      this.afterPerform(whatToDo)
    }
  }

  private get hasCable (): boolean {
    return !!this.cable._cable
  }

  private get hasSubscriptions (): boolean {
    return this.hasCable && this.cable._cable.subscriptions.subscriptions.length > 0
  }

  private disconnectIfNotSubscribed (): void {
    if (this.hasCable && !this.hasSubscriptions) {
      this.log('closing web socket', 'debug')
      this.cable._cable.connection.webSocket.close()
    }
  }

  private log (message: string, levelRequired: string) {
    if (this.debugLevel === levelRequired) {
      console.log(`ActionCableService: ${message}`)
    }
  }

}